if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.checkoutButton = this.querySelector("[data-checkout-now]");
        if (this.checkoutButton) {
          this.checkoutButton.addEventListener("click", this.onCheckoutHandler.bind(this));
        }
        this.minicart = document.querySelector("mini-cart");
        this.quickViewDrawer = this.closest("quick-view-drawer");
        this.eventContext = this.form.dataset.eventContext;
        this.hideErrors = this.dataset.hideErrors === "true";
      }

      connectedCallback() {
        this.onGateContinueHandler = this.onGateContinue.bind(this);
        document.addEventListener("cart-email-gate-continue", this.onGateContinueHandler);
        this.initCustomTextureConsent();
        this.resumePendingAddToCart();
      }

      disconnectedCallback() {
        document.removeEventListener("cart-email-gate-continue", this.onGateContinueHandler);
        this.teardownCustomTextureConsent();
      }

      initCustomTextureConsent() {
        this.consentWrap = this.querySelector("[data-custom-texture-consent]");
        this.consentInput = this.querySelector("[data-custom-texture-consent-input]");
        this.customTextureGroupActive = false;

        if (!this.consentWrap) {
          return;
        }

        this.onTextureGroupChange = this.onTextureGroupChange.bind(this);
        this.onConsentInputChange = this.onConsentInputChange.bind(this);
        this.onVariantChangeForConsent = this.onVariantChangeForConsent.bind(this);
        this.onConsentBlockedClick = this.onConsentBlockedClick.bind(this);

        this.textureGroupRoot = this.closest(".product") || this;
        this.textureGroupRoot.addEventListener("texture-group-change", this.onTextureGroupChange);
        this.consentInput?.addEventListener("change", this.onConsentInputChange);
        document.addEventListener("product:variant-change", this.onVariantChangeForConsent);
        this.addEventListener("click", this.onConsentBlockedClick);

        this.syncInitialTextureGroup();
        customElements.whenDefined("product-texture-picker").then(() => {
          requestAnimationFrame(() => this.syncInitialTextureGroup());
        });
      }

      teardownCustomTextureConsent() {
        if (!this.consentWrap) {
          return;
        }

        this.textureGroupRoot?.removeEventListener("texture-group-change", this.onTextureGroupChange);
        this.consentInput?.removeEventListener("change", this.onConsentInputChange);
        document.removeEventListener("product:variant-change", this.onVariantChangeForConsent);
        this.removeEventListener("click", this.onConsentBlockedClick);
      }

      onConsentBlockedClick(event) {
        if (!this.isCustomTextureConsentBlocking()) {
          return;
        }

        const button = event.target.closest(".button-add-to-cart, .button-checkout-now");
        if (!button || !this.contains(button)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.shakeCustomTextureConsent();
      }

      shakeCustomTextureConsent() {
        if (!this.consentWrap || this.consentWrap.hidden) {
          return;
        }

        this.consentWrap.classList.remove("is-shaking");
        void this.consentWrap.offsetWidth;
        this.consentWrap.classList.add("is-shaking");
        this.consentWrap.addEventListener(
          "animationend",
          () => {
            this.consentWrap.classList.remove("is-shaking");
          },
          { once: true },
        );

        this.consentInput?.focus({ preventScroll: true });
      }

      syncInitialTextureGroup() {
        const picker = this.closest(".product")?.querySelector("product-texture-picker");
        const selected = picker?.querySelector(".product-texture-picker-option.is-selected");
        const group = selected?.dataset.textureGroup || "default";
        this.applyTextureGroup(group);
      }

      onTextureGroupChange(event) {
        this.applyTextureGroup(event.detail?.group || "default");
      }

      onConsentInputChange() {
        this.updatePurchaseButtons();
      }

      onVariantChangeForConsent() {
        this.updatePurchaseButtons();
      }

      applyTextureGroup(group) {
        this.customTextureGroupActive = group === "custom";

        if (!this.consentWrap) {
          return;
        }

        if (this.customTextureGroupActive) {
          this.consentWrap.hidden = false;
          if (this.consentInput) {
            this.consentInput.checked = false;
          }
        } else {
          this.consentWrap.hidden = true;
          if (this.consentInput) {
            this.consentInput.checked = false;
          }
        }

        this.updatePurchaseButtons();
      }

      isVariantAvailable() {
        const variantInput = this.form?.querySelector('[name="id"]');
        return Boolean(variantInput && !variantInput.disabled);
      }

      isCustomTextureConsentBlocking() {
        return (
          this.customTextureGroupActive &&
          this.consentInput &&
          !this.consentInput.checked
        );
      }

      updatePurchaseButtons() {
        if (!this.consentWrap) {
          return;
        }

        const blockPurchase = this.isCustomTextureConsentBlocking();
        const variantAvailable = this.isVariantAvailable();

        const buttons = [
          this.querySelector('[type="submit"]'),
          this.querySelector("[data-checkout-now]"),
        ];

        buttons.forEach((button) => {
          if (!button) {
            return;
          }

          const consentBlock = blockPurchase && variantAvailable;

          if (!variantAvailable) {
            button.disabled = true;
            button.setAttribute("disabled", "");
            button.setAttribute("aria-disabled", "true");
            button.classList.remove("button--custom-texture-consent-required");
            return;
          }

          if (consentBlock) {
            button.disabled = false;
            button.removeAttribute("disabled");
            button.setAttribute("aria-disabled", "true");
            button.classList.add("button--custom-texture-consent-required");
            return;
          }

          button.disabled = false;
          button.removeAttribute("disabled");
          button.removeAttribute("aria-disabled");
          button.classList.remove("button--custom-texture-consent-required");
        });

        document.dispatchEvent(
          new CustomEvent("product:purchase-gate-change", { bubbles: true }),
        );
      }

      onSubmitHandler(event) {
        event.preventDefault();
        const submitButton = this.querySelector('[type="submit"]');
        if (submitButton.classList.contains("loading")) {
          return;
        }

        if (this.isCustomTextureConsentBlocking()) {
          this.shakeCustomTextureConsent();
          return;
        }

        this.requestCartEmailThenAddToCart(submitButton);
      }

      async requestCartEmailThenAddToCart(triggerButton, options = {}) {
        const gate = document.querySelector("cart-email-gate");

        if (gate?.isEnabled?.() && !gate.hasEmail?.()) {
          if (!customElements.get("cart-email-gate")) {
            await customElements.whenDefined("cart-email-gate");
          }

          if (gate.openForAddToCart(triggerButton)) {
            return;
          }
        }

        this.addToCart(triggerButton, options);
      }

      resumePendingAddToCart() {
        this.completePendingAddToCart();
      }

      onGateContinue() {
        this.completePendingAddToCart();
      }

      completePendingAddToCart() {
        let pendingAdd = false;

        try {
          pendingAdd = sessionStorage.getItem("cartEmailGatePendingAdd") === "true";
        } catch {
          return;
        }

        if (!pendingAdd) {
          return;
        }

        const gate = document.querySelector("cart-email-gate");

        if (gate?.dataset.formErrors === "true") {
          try {
            sessionStorage.removeItem("cartEmailGatePendingAdd");
          } catch {
            // Ignore storage errors.
          }
          return;
        }

        try {
          sessionStorage.removeItem("cartEmailGatePendingAdd");
        } catch {
          return;
        }

        const submitButton = this.querySelector('[type="submit"]');

        if (!submitButton || submitButton.classList.contains("loading")) {
          return;
        }

        requestAnimationFrame(() => {
          this.requestCartEmailThenAddToCart(submitButton);
        });
      }

      onCheckoutHandler(event) {
        event.preventDefault();
        const checkoutButton = this.checkoutButton;

        if (!checkoutButton || checkoutButton.classList.contains("loading")) {
          return;
        }

        if (this.isCustomTextureConsentBlocking()) {
          this.shakeCustomTextureConsent();
          return;
        }

        this.addToCart(checkoutButton, { redirectToCheckout: true });
      }

      addToCart(triggerButton, { redirectToCheckout = false } = {}) {
        if (this.isCustomTextureConsentBlocking()) {
          return;
        }

        this.handleErrorMessage();

        if (!this.minicart) {
          this.minicart = document.querySelector("mini-cart");
        }

        triggerButton.setAttribute("aria-disabled", true);
        triggerButton.classList.add("loading");
        triggerButton.querySelector(".button-overlay-spinner")?.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        const cartEmail = this.getCartEmail();

        if (cartEmail) {
          formData.append("attributes[Customer Email]", cartEmail);
        }

        if (this.minicart) {
          formData.append(
            "sections",
            this.minicart.getSectionsToRender().map((section) => section.section),
          );
        }
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);
              this.minicart?.updateSectionContents();
              return;
            }

            this.syncCartEmailAttribute();

            if (redirectToCheckout) {
              const checkoutUrl = window.routes?.checkout_url || "/checkout";
              window.location.assign(checkoutUrl);
              return;
            }

            this.minicart?.renderContents(response);
            this.openMiniCart(triggerButton);

            const event = new CustomEvent("cart:item-add", {
              detail: {
                items: [response],
                context: this.eventContext,
              },
            });
            document.dispatchEvent(event);

            publish(PUB_SUB_EVENTS.cartItemAdd, {
              source: this.eventContext,
              items: [response],
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            triggerButton.classList.remove("loading");
            triggerButton.removeAttribute("aria-disabled");
            triggerButton.querySelector(".button-overlay-spinner")?.classList.add("hidden");
          });
      }

      openMiniCart(triggerButton) {
        if (window.themeSettings?.redirectToCart) {
          location.href = window.routes.cart_url;
          return;
        }

        if (!this.minicart) {
          this.minicart = document.querySelector("mini-cart");
        }

        if (this.quickViewDrawer) {
          this.quickViewDrawer.close();
          document.body.classList.add("drawer-open");
          setTimeout(() => this.minicart?.open(), 200);
          return;
        }

        if (this.minicart) {
          this.minicart.open(triggerButton);
          return;
        }

        document.dispatchEvent(
          new CustomEvent("open-mini-cart", {
            detail: { opener: triggerButton },
          }),
        );
      }

      getCartEmail() {
        const gate = document.querySelector("cart-email-gate");
        let email = gate?.getEmail?.() || null;

        if (!email && typeof window.cartEmailGateEmail === "string") {
          email = window.cartEmailGateEmail.trim() || null;
        }

        if (!email) {
          try {
            email = sessionStorage.getItem("cartEmailGateEmail");
          } catch {
            email = null;
          }
        }

        return email;
      }

      syncCartEmailAttribute() {
        const email = this.getCartEmail();

        if (!email || !window.routes?.cart_update_url) {
          return;
        }

        fetch(window.routes.cart_update_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            attributes: {
              "Customer Email": email,
            },
            note: `Cart lead: ${email}`,
          }),
        }).catch((error) => {
          console.warn("Cart email attribute sync failed:", error);
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form-error-message-wrapper");
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form-error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );

  document.querySelectorAll(".form-status-dismiss").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      button.closest(".form-status").setAttribute("hidden", true);
    });
  });
}
